import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import PerformanceMonitor from '@/components/PerformanceMonitor';
import '@/App.css';

// Eager load critical landing pages (needed immediately)
import LandingPage from '@/pages/LandingPage';
import RoofersLandingPageNew from '@/pages/RoofersLandingPageNew';
import RoofersLandingPagePaid from '@/pages/RoofersLandingPagePaid';
import FreePreviewSuccess from '@/pages/FreePreviewSuccess';

// Lazy load auth pages (only needed when user navigates to them)
const SignupPage = lazy(() => import('@/pages/SignupPage'));
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));

// Lazy load dashboard pages (only needed after authentication)
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const BuildLeads = lazy(() => import('@/pages/BuildLeads'));
const PackageSelection = lazy(() => import('@/pages/PackageSelection'));
const ReviewOrder = lazy(() => import('@/pages/ReviewOrder'));
const SuccessPage = lazy(() => import('@/pages/SuccessPage'));
const PaymentSuccess = lazy(() => import('@/pages/PaymentSuccess'));
const MyOrders = lazy(() => import('@/pages/MyOrders'));
const BillingPage = lazy(() => import('@/pages/BillingPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));

// Lazy load static pages
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-slate-600">Loading...</p>
    </div>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { token } = useAuth();
  return token ? children : <Navigate to="/login" />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PerformanceMonitor />
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/roofers" element={<RoofersLandingPageNew />} />
            <Route path="/roofers-paid" element={<RoofersLandingPagePaid />} />
            <Route path="/free-preview-success" element={<FreePreviewSuccess />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/dashboard/orders" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
            <Route path="/dashboard/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
            <Route path="/dashboard/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/dashboard/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/leads/new" element={<ProtectedRoute><BuildLeads /></ProtectedRoute>} />
            <Route path="/leads/package" element={<ProtectedRoute><PackageSelection /></ProtectedRoute>} />
            <Route path="/leads/review" element={<ProtectedRoute><ReviewOrder /></ProtectedRoute>} />
            <Route path="/leads/success" element={<ProtectedRoute><SuccessPage /></ProtectedRoute>} />
            <Route path="/leads/payment-success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
          </Routes>
        </Suspense>
        <Toaster />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
